/**
 * AB#650 — the three blueprint columns (Account | Site | User) and their tiles.
 *
 * A tile is a plain focusable <button> so Enter/Space select it. Its menu opens
 * by right-click or long-press (Base UI ContextMenu handles both); the tile shows
 * `cursor: context-menu` so that's discoverable.
 */

import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import { Eye, EyeOff, LayoutGrid, List, Star } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '@/components/ui/context-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  menuItems,
  type Blueprint,
  type BlueprintColumn,
  type MenuItem,
  type TileActions,
} from './blueprint';
export type { DuplicateTarget, TileActions } from './blueprint';
import { BlueprintSwatch } from './BlueprintSwatch';

function renderItems(items: MenuItem[]) {
  return items.map((item) =>
    item.items ? (
      <ContextMenuSub key={item.label}>
        <ContextMenuSubTrigger>{item.label}</ContextMenuSubTrigger>
        <ContextMenuSubContent>{renderItems(item.items)}</ContextMenuSubContent>
      </ContextMenuSub>
    ) : (
      <Fragment key={item.label}>
        {item.destructive && <ContextMenuSeparator />}
        <ContextMenuItem
          variant={item.destructive ? 'destructive' : 'default'}
          disabled={item.disabled}
          title={item.title}
          onClick={item.onClick}
        >
          {item.label}
        </ContextMenuItem>
      </Fragment>
    ),
  );
}

function BlueprintTile({
  blueprint,
  selected,
  applied,
  view,
  onSelect,
  actions,
}: {
  blueprint: Blueprint;
  selected: boolean;
  /** `settings.account_theme_id` points here — the star, and what the site builds. */
  applied: boolean;
  view: BlueprintView;
  onSelect: () => void;
  actions: TileActions;
}) {
  const items = menuItems(blueprint, applied, actions);
  const list = view === 'list';

  return (
    <ContextMenu>
      <ContextMenuTrigger className={cn('cursor-context-menu', list && 'block')}>
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          title={blueprint.name}
          // The selection ring is a box-shadow (`ring-*`), so selecting never
          // changes the tile's size or nudges its neighbours.
          className={cn(
            'wpp-tile group/tile cursor-context-menu transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
            list
              ? 'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left'
              : 'flex w-28 flex-col items-center gap-2 rounded-lg p-3 text-center',
            selected && 'bg-accent/40 ring-2 ring-primary',
          )}
        >
          <BlueprintSwatch
            blueprint={blueprint}
            selected={selected}
            isDefault={applied}
            size={list ? 'sm' : 'md'}
          />
          {list ? (
            // Rows are as wide as the column, so a plain truncate + title is enough.
            // The swatch's star is too small at this size to carry "this is the
            // site theme" on its own, so the row says it outright.
            <>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{blueprint.name}</span>
              {applied && (
                <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
                  <Star className="size-3.5 fill-current" aria-hidden />
                  Site theme
                </span>
              )}
            </>
          ) : (
            <span className="block w-full overflow-hidden text-xs font-medium whitespace-nowrap">
              <span
                className="wpp-tile-name inline-block"
                // Scrolls while hovered, focused or selected, at a constant speed.
                style={
                  {
                    '--wpp-marquee-dur': `${Math.max(4, blueprint.name.length * 0.15)}s`,
                  } as CSSProperties
                }
              >
                {blueprint.name}
              </span>
            </span>
          )}
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent>{renderItems(items)}</ContextMenuContent>
    </ContextMenu>
  );
}

/**
 * Marquee for tile names too wide for the tile. Pure CSS, no measuring: the end
 * offset is `min(0px, textWidth*-1 + tileTextWidth)`, so a name that already fits
 * animates to 0 — i.e. doesn't move. `alternate` scrolls back instead of jumping.
 * The SELECTED tile scrolls too (`aria-pressed`), since that's the name you are
 * most likely trying to read.
 *
 * Reduced motion still reveals the name, just once and slowly, with no loop.
 * ponytail: 5.5rem = w-28 (7rem) minus p-3 either side; keep them in step.
 */
const TILE_CSS = `
@keyframes wpp-marquee {
  from { transform: translateX(0); }
  to { transform: translateX(min(0px, calc(-100% + 5.5rem))); }
}
.wpp-tile:hover .wpp-tile-name,
.wpp-tile:focus-visible .wpp-tile-name,
.wpp-tile[aria-pressed='true'] .wpp-tile-name {
  animation: wpp-marquee var(--wpp-marquee-dur, 6s) linear infinite alternate;
}
/* Muted unless /user/appearance sets html[data-motion="always"] (see index.css). */
@media (prefers-reduced-motion: reduce) {
  :root:not([data-motion="always"]) .wpp-tile-name { transition: transform var(--wpp-marquee-dur, 6s) linear; }
  :root:not([data-motion="always"]) .wpp-tile:hover .wpp-tile-name,
  :root:not([data-motion="always"]) .wpp-tile:focus-visible .wpp-tile-name,
  :root:not([data-motion="always"]) .wpp-tile[aria-pressed='true'] .wpp-tile-name {
    animation: none;
    transform: translateX(min(0px, calc(-100% + 5.5rem)));
  }
}
`;

/** Tiles or rows, for every column at once. Persisted per browser. */
export type BlueprintView = 'grid' | 'list';

const VIEW_KEY = 'wpp-blueprints-view';

function storedView(): BlueprintView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid'; // private mode / storage disabled
  }
}

function Column({
  title,
  hint,
  action,
  header,
  menu,
  blueprints,
  selectedKey,
  appliedKey,
  view,
  onSelect,
  actions,
}: {
  title: string;
  /** One-line empty state. */
  hint: string;
  /** Empty-state call to action (e.g. Fork, New Theme). */
  action?: ReactNode;
  /** Always-visible column controls (e.g. the bundled theme picker). */
  header?: ReactNode;
  /** Right-click menu for the column's empty area. */
  menu: MenuItem[];
  blueprints: Blueprint[];
  selectedKey: string | null;
  /** Key of the blueprint this site builds with, if it is in this column. */
  appliedKey: string | null;
  view: BlueprintView;
  onSelect: (bp: Blueprint) => void;
  actions: TileActions;
}) {
  return (
    <section className="flex flex-col">
      <div className="mb-2 flex min-h-8 flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">{title}</h2>
        {header}
      </div>
      {/* A tile's own trigger stops the contextmenu event (Base UI `stopEvent`),
          so right-clicking a tile never also opens this column menu. */}
      <ContextMenu>
        <ContextMenuTrigger className="min-h-44 flex-1 cursor-context-menu rounded-lg border p-3">
          {blueprints.length === 0 ? (
            <div className="flex h-full flex-col items-start justify-center gap-3 py-4">
              <p className="text-sm text-muted-foreground">{hint}</p>
              {action}
            </div>
          ) : (
            <div className={cn(view === 'list' ? 'flex flex-col gap-0.5' : 'flex flex-wrap gap-2')}>
              {blueprints.map((bp) => (
                <BlueprintTile
                  key={bp.key}
                  blueprint={bp}
                  selected={bp.key === selectedKey}
                  applied={bp.key === appliedKey}
                  view={view}
                  onSelect={() => onSelect(bp)}
                  actions={actions}
                />
              ))}
            </div>
          )}
        </ContextMenuTrigger>
        <ContextMenuContent>{renderItems(menu)}</ContextMenuContent>
      </ContextMenu>
    </section>
  );
}

export function BlueprintColumns({
  byColumn,
  selectedKey,
  appliedKey,
  onSelect,
  actions,
  siteAction,
  newBlueprintButton,
  onNewBlueprint,
  stockHidden,
  onToggleStock,
}: {
  byColumn: Record<BlueprintColumn, Blueprint[]>;
  selectedKey: string | null;
  /** Key of the blueprint this site builds with — the starred tile. */
  appliedKey: string | null;
  onSelect: (bp: Blueprint) => void;
  actions: TileActions;
  /** Site-column empty state CTA — its only per-column button. */
  siteAction: ReactNode;
  /** The page's single "New Theme" button, far right of the header row. */
  newBlueprintButton: ReactNode;
  /** Right-click "New Theme" in a column: scope is fixed to that column. */
  onNewBlueprint: (scope: BlueprintColumn) => void;
  /** AB#644: are the stock tiles hidden from the Account column right now? */
  stockHidden: boolean;
  onToggleStock: () => void;
}) {
  const [view, setView] = useState<BlueprintView>(storedView);

  const toggleView = () => {
    const next: BlueprintView = view === 'grid' ? 'list' : 'grid';
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // storage disabled — the choice just doesn't survive a reload
    }
  };

  const viewToggle = (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={toggleView}
      aria-pressed={view === 'list'}
      aria-label={view === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
      title={view === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
    >
      {view === 'grid' ? <List className="size-4" /> : <LayoutGrid className="size-4" />}
    </Button>
  );

  const newItem = (scope: BlueprintColumn): MenuItem[] => [
    { label: 'New Theme', onClick: () => onNewBlueprint(scope) },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <style>{TILE_CSS}</style>
      <Column
        title="Account"
        hint="Shared with every seat"
        menu={newItem('account')}
        header={
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggleStock}
            aria-pressed={stockHidden}
            aria-label={stockHidden ? 'Show stock themes' : 'Hide stock themes'}
            title={stockHidden ? 'Show stock themes' : 'Hide stock themes'}
          >
            {stockHidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </Button>
        }
        blueprints={byColumn.account}
        selectedKey={selectedKey}
        appliedKey={appliedKey}
        view={view}
        onSelect={onSelect}
        actions={actions}
      />
      <Column
        title="Site"
        hint="Owned by this site only"
        action={siteAction}
        menu={newItem('site')}
        blueprints={byColumn.site}
        selectedKey={selectedKey}
        appliedKey={appliedKey}
        view={view}
        onSelect={onSelect}
        actions={actions}
      />
      <Column
        title="User"
        hint="Yours, on every account — duplicate to Account to use as a site default"
        menu={newItem('user')}
        header={
          <div className="flex items-center gap-1">
            {newBlueprintButton}
            {viewToggle}
          </div>
        }
        blueprints={byColumn.user}
        selectedKey={selectedKey}
        appliedKey={appliedKey}
        view={view}
        onSelect={onSelect}
        actions={actions}
      />
    </div>
  );
}
